"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Image from "next/image";
import Link from "next/link";
import { useRef, type ReactNode, type RefObject } from "react";

import { resolvePageImage, type SitePageImageMap } from "@/lib/site-page-images";

gsap.registerPlugin(ScrollTrigger);

const accent = "text-[#ff5c00]";

function CaseStudyCard({
  title,
  children,
  href,
}: {
  title: string;
  children: ReactNode;
  href: string;
}) {
  return (
    <article className="flex h-full min-h-0 flex-col overflow-y-auto overscroll-contain rounded-[1.75rem] bg-white p-6 shadow-sm sm:p-8 md:rounded-4xl md:p-10 lg:rounded-[2.25rem]">
      <p className={`text-2xl font-medium leading-none sm:text-3xl md:text-4xl ${accent}`} aria-hidden>
        *
      </p>
      <h3 className={`mt-4 text-xl font-semibold tracking-tight sm:mt-6 sm:text-2xl md:text-3xl ${accent}`}>
        {title}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#231f20]/90 sm:mt-4 sm:text-base md:text-lg">
        {children}
      </p>
      <Link
        href={href}
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#231f20] underline-offset-4 transition-opacity hover:opacity-70 sm:mt-6 sm:text-base md:mt-8 md:text-lg lg:mt-auto lg:pt-6"
      >
        <span aria-hidden className="font-normal">
          &gt;
        </span>
        View Case Study
      </Link>
    </article>
  );
}

function ImageCard({
  src,
  alt,
  priority,
  loading,
  slideRef,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  /** When not `priority`, use `eager` for large above-the-fold images (LCP). */
  loading?: "eager" | "lazy";
  slideRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={slideRef}
      className="relative h-full min-h-0 w-full overflow-hidden rounded-[1.75rem] bg-[#f5f2f0] will-change-transform md:rounded-4xl lg:rounded-[2.25rem]"
    >
      <div className="relative h-full min-h-0 w-full">
        <Image
          src={src}
          alt={alt}
          fill
          priority={priority}
          loading={priority ? undefined : loading}
          className="object-cover"
          sizes="(max-width: 1023px) 100vw, 33vw"
        />
      </div>
    </div>
  );
}

/** 首页「网站设计开发」区块：文案 + 案例马赛克与滚动视差图 */
export default function WebDesignDevSection({
  pageImageMap,
}: {
  pageImageMap?: SitePageImageMap;
}) {
  const sealordImg = resolvePageImage("home-web-design-sealord", pageImageMap);
  const matthewsImg = resolvePageImage("home-web-design-matthews", pageImageMap);

  const scope = useRef<HTMLElement | null>(null);
  const topRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const bottomRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const topSlideRef = useRef<HTMLDivElement | null>(null);
  const bottomSlideRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const topRowTrigger = topRowTriggerRef.current;
      const bottomRowTrigger = bottomRowTriggerRef.current;
      const topSlide = topSlideRef.current;
      const bottomSlide = bottomSlideRef.current;
      if (!topRowTrigger || !bottomRowTrigger || !topSlide || !bottomSlide) return;

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          isDesktop: "(min-width: 1024px)",
        },
        (context) => {
          const { reduceMotion, isDesktop } = context.conditions as {
            reduceMotion: boolean;
            isDesktop: boolean;
          };

          /** 滚动结束后的横向停留位置（相对自身宽度 %） */
          const restTop = 0;
          const restBottom = 0;

          // 非桌面：不做位移动画
          if (!isDesktop) {
            gsap.set(topSlide, { xPercent: 0 });
            gsap.set(bottomSlide, { xPercent: 0 });
            return;
          }
          // 系统偏好减少动效：直接落在静止位
          if (reduceMotion) {
            gsap.set(topSlide, { xPercent: restTop });
            gsap.set(bottomSlide, { xPercent: restBottom });
            return;
          }

          /** 每行共用：视口进入区间内 scrub 联动 */
          const rowScrollTrigger = (trigger: HTMLElement) => ({
            trigger,
            start: "top 88%",
            end: "top 60%",
            scrub: 1.15,
            invalidateOnRefresh: true,
          });

          // 上行中间图：从右侧滑入至静止位
          gsap.fromTo(
            topSlide,
            { xPercent: 109 },
            {
              xPercent: restTop,
              ease: "none",
              scrollTrigger: rowScrollTrigger(topRowTrigger),
            }
          );
          // 下行中间图：从左侧滑入至静止位
          gsap.fromTo(
            bottomSlide,
            { xPercent: -109 },
            {
              xPercent: restBottom,
              ease: "none",
              scrollTrigger: rowScrollTrigger(bottomRowTrigger),
            }
          );
        }
      );

      return () => {
        mm.revert(); // 卸载 matchMedia 注册的动画与监听器
      };
    },
    { scope, dependencies: [] }
  );

  return (
    <section
      ref={scope}
      className="relative bg-[#b5adaa] pt-16 pb-20 font-sans text-[#231f20] md:pt-20 md:pb-28"
      aria-labelledby="web-design-dev-heading"
    >
      <div className="site-content">
        {/* 区块标题与导语：大标题 + 右侧说明 */}
        <div className="mt-[clamp(3rem,18vw,31.25rem)] grid grid-cols-1 gap-8 lg:mt-[clamp(26rem,26vw,42rem)] lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[550px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#231f20]/80 md:text-base">
              Website design and development
            </p>
            <h2
              id="web-design-dev-heading"
              className="mt-4 text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              And that&apos;s where the journey starts.{" "}
              <span className={accent}>On your website</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className={`text-4xl font-medium leading-none md:text-5xl ${accent}`} aria-hidden>
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-[#231f20]/85" />
            <p className="mt-6 text-base leading-relaxed text-[#231f20]/95 md:text-lg">
              Your site is where curiosity becomes conviction — clear story,
              fast experience, and craft that reflects how seriously you take
              the work. We design and build sites that feel as considered as the
              brands behind them.
            </p>
          </div>
        </div>

        {/*
          移动端：每组「上图下文案」，四块统一方形比例；文案区可滚动。
          lg+：三列棋盘格，中间竖图、两侧案例卡，桌面端白卡略 translate 与图叠压。
        */}
        <div className="mt-14 flex flex-col gap-10 md:mt-20 md:gap-12 lg:grid lg:grid-cols-3 lg:items-start lg:gap-x-8 lg:gap-y-10 lg:overflow-visible lg:min-h-[clamp(26rem,34vw,40rem)] lg:grid-rows-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-x-10 xl:gap-y-10 xl:min-h-[clamp(28rem,32vw,42rem)]">
          {/* 第一行：Sealord — 图 + 案例卡；lg:contents 让子项参与外层 grid */}
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={topRowTriggerRef}
              className="aspect-square w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-1 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={topSlideRef}
                priority
                src={sealordImg.src}
                alt={sealordImg.alt}
              />
            </div>
            <div className="aspect-square w-full shrink-0 lg:relative lg:z-30 lg:col-start-3 lg:row-start-1 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none lg:-translate-x-[14%] xl:translate-x-0">
              <CaseStudyCard title="Sealord Skipper" href="/case-studies/building-a-launch-platform-for-sealord-skipper-pet-food">
                Brand-led storytelling for a seafood legacy — balancing heritage
                with modern commerce, photography-led layouts, and journeys that
                move people from discovery to the cart with confidence.
              </CaseStudyCard>
            </div>
          </div>
          {/* 第二行：Matthews — 图 + 案例卡 */}
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={bottomRowTriggerRef}
              className="aspect-square w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-2 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={bottomSlideRef}
                loading="eager"
                src={matthewsImg.src}
                alt={matthewsImg.alt}
              />
            </div>
            <div className="aspect-square w-full shrink-0 lg:relative lg:z-30 lg:col-start-1 lg:row-start-2 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none lg:translate-x-[14%] xl:translate-x-0">
              <CaseStudyCard title="Matthews Sports Vision" href="/case-studies/building-a-performance-driven-ecommerce-platform-for-matthews-sports-vision">
                A performance-minded digital presence for athletes and coaches:
                sharp typography, motion that feels athletic, and content structure
                that puts programs and results first.
              </CaseStudyCard>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
